import { useState } from "react";
import { Link } from "react-router-dom";
import FilterChip from "shared/FilterChip";
import Modal from "shared/Modal";
import StatusPill from "shared/StatusPill";
import type { OutreachPath } from "../api/OutreachTouchesApi";
import { CompanyTimeline, useTimeline } from "./OutreachTimeline";
import PathActivityFeed from "./PathActivityFeed";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

/** One path's full timeline (emails and LinkedIn steps in date order, with the company lock), opened from a Pipeline row. */
export default function PathDetailModal({ path, onClose }: { path: OutreachPath; onClose: () => void }) {
  const [view, setView] = useState<"timeline" | "activity">("timeline");
  const { data, failed } = useTimeline(path.opportunity_id, path.prime_company_id);
  const company = data?.companies.find((c) => c.prime_company_id === path.prime_company_id);
  return (
    <Modal size="lg" title={path.company_name ?? "Company"} description={path.rfp_title ?? undefined} onClick={onClose} closeButtonCLick={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {[path.contact_name, path.contact_title, path.contact_email].filter(Boolean).join(" · ") || "No contact chosen yet"}
          {path.prime_contact_id && <> · <Link className="underline underline-offset-2" to={`${crmBase()}/prime-contacts/${path.prime_contact_id}`}>contact page</Link></>}
          {" · "}<Link className="underline underline-offset-2" to={`${crmBase()}/companies/${path.prime_company_id}`}>company page</Link>
        </p>
        {path.conversation && (
          <p className="text-sm">
            <StatusPill tone={path.conversation.open ? "info" : "neutral"} size="sm">{path.conversation.open ? "conversation open" : "conversation closed"}</StatusPill>
            {path.conversation.outcome && <span className="ml-2 text-muted-foreground">outcome: {path.conversation.outcome.replace("_", " ")}</span>}
            {" "}<Link className="underline underline-offset-2 text-xs" to={`${crmBase()}/due-today?stage=conversations`}>open Conversations</Link>
          </p>
        )}
        <div className="flex gap-2" role="group" aria-label="What to show">
          <FilterChip active={view === "timeline"} onClick={() => setView("timeline")}>Timeline</FilterChip>
          <FilterChip active={view === "activity"} onClick={() => setView("activity")}>Activity</FilterChip>
        </div>
        {view === "activity" ? <PathActivityFeed opportunityId={path.opportunity_id} companyId={path.prime_company_id} /> : (
          <>
          {failed && <p className="text-sm text-muted-foreground">Could not load the timeline.</p>}
          {!failed && !data && <p className="text-sm text-muted-foreground">Loading the timeline…</p>}
          {data && !company && <p className="text-sm text-muted-foreground">No timeline is on record for this company yet.</p>}
          {data && company && <CompanyTimeline company={company} drafted={data.drafted} />}
          </>
        )}
      </div>
    </Modal>
  );
}
